/**
 * The outreach exchange on an alert (Story 35, task 138; task 139f): who was
 * asked, on which channels and whether each send went, what they answered and
 * how a yes was confirmed, the security contact when they were brought in,
 * and how it ended. Read-only: nothing here changes the exchange.
 */

import type { AlertOutreach, OutreachDelivery, OutreachParty } from '@bluesphere/api-client';

import { timeAgo } from './format.js';

const RESULT_TEXT: Record<string, string> = {
  no: 'Said it was not them — promoted to actionable',
  yes: 'Confirmed it was them — resolved',
  unsure: 'Not sure',
  timeout: 'No answer in time',
  unconfirmed: 'The yes could not be confirmed',
  superseded: 'Closed before an answer — no action',
  skipped: 'Not asked',
};

const ROLE_TEXT: Record<string, string> = {
  user: 'User',
  manager: 'Manager',
  contact: 'Security contact',
};

const ROLE_ORDER = ['user', 'manager', 'contact'];

const OUTCOME_TEXT: Record<string, string> = {
  actionable: 'the alert became actionable',
  resolve: 'the alert was resolved',
  escalate: 'the alert stays open for an analyst',
  none: 'nothing changed',
};

/** Where an answer was given or a message went, as words. */
const CHANNEL_TEXT: Record<string, string> = {
  email: 'email',
  sms: 'text',
  voice: 'phone call',
  teams: 'Microsoft Teams',
  slack: 'Slack',
  whatsapp: 'WhatsApp',
};

/** The confirmation, by the phone channel that carried it. */
const CONFIRM_TEXT: Record<string, string> = { sms: 'text', voice: 'call' };

const ERROR_TEXT: Record<string, string> = {
  not_configured: 'not set up',
  not_in_conversation: 'Teams chat not ready',
  no_phone: 'no phone on file',
};

function channel(name: string | null | undefined): string {
  return name ? (CHANNEL_TEXT[name] ?? name) : '';
}

/** The party's sends; absent from an API older than task 139f. */
function sendsOf(p: OutreachParty): OutreachDelivery[] {
  return p.deliveries ?? [];
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The confirmation's channel: the one that confirmed, else the one asked on. */
function confirmChannel(p: OutreachParty): string {
  const asked = sendsOf(p)
    .filter((d) => d.purpose === 'confirm')
    .at(-1)?.channel;
  const via = p.confirmed_via ?? asked;
  return via ? (CONFIRM_TEXT[via] ?? channel(via)) : 'phone';
}

export function answerText(p: OutreachParty, concluded: boolean): string {
  if (!p.answer) return concluded ? 'No answer' : 'No answer yet';
  if (p.answer === 'yes') {
    // Before task 139f the confirming reply overwrote `answered_via`, so a
    // confirmed yes without `confirmed_via` was confirmed on that channel.
    const confirmedVia = p.confirmed ? (p.confirmed_via ?? p.answered_via) : p.confirmed_via;
    const on =
      p.answered_via && p.answered_via !== confirmedVia ? ` on ${channel(p.answered_via)}` : '';
    if (p.confirmed) {
      return `Yes${on} — confirmed by ${channel(confirmedVia) || 'phone'}`;
    }
    return p.confirm_requested_at && !concluded
      ? `Yes${on} — waiting for the confirmation ${confirmChannel(p)}`
      : `Yes${on} — not confirmed`;
  }
  const said = p.answer === 'no' ? 'No' : 'Not sure';
  return p.answered_via ? `${said} on ${channel(p.answered_via)}` : said;
}

function Send({ d, label }: { d: OutreachDelivery; label: string }) {
  const why = d.error ? (ERROR_TEXT[d.error] ?? d.error) : 'not sent';
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ' +
        (d.ok
          ? 'bg-bluesphere-surfaceMuted text-bluesphere-text'
          : 'bg-bluesphere-surfaceMuted text-bluesphere-muted line-through decoration-1')
      }
      title={d.ok ? `${label}: sent` : `${label}: ${why}`}
    >
      {label} {d.ok ? '✓' : `✗ ${why}`}
    </span>
  );
}

/** One chip per send, the question first; the confirmation and the contact's telling after. */
export function Sends({ party }: { party: OutreachParty }) {
  const sends = sendsOf(party);
  if (sends.length === 0) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {sends.map((d, i) => {
        const name = channel(d.channel);
        const label =
          d.purpose === 'confirm'
            ? `Confirmation ${CONFIRM_TEXT[d.channel] ?? name}`
            : d.purpose === 'told'
              ? `Told the result by ${name}`
              : capitalized(name);
        return <Send key={`${d.purpose}-${d.channel}-${i}`} d={d} label={label} />;
      })}
    </span>
  );
}

export function OutreachPanel({ outreach }: { outreach: AlertOutreach }) {
  const c = outreach.conclusion;
  const parties = [...outreach.parties].sort(
    (a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role),
  );
  return (
    <section className="bs-card p-5">
      <h2 className="mb-1 text-xl font-bold lg:text-2xl">Outreach</h2>
      <p className="mb-3 text-sm text-bluesphere-textSecondary">
        {outreach.question.what ? (
          <>
            Asked about <strong>{outreach.question.what}</strong>
            {outreach.question.when ? `, ${outreach.question.when}` : ''}.
          </>
        ) : (
          'The rule asked the user whether this was them.'
        )}
      </p>
      {c ? (
        <p className="mb-3 rounded-input bg-bluesphere-surfaceMuted px-3 py-2 text-sm">
          <strong>{RESULT_TEXT[c.result] ?? c.result}</strong>
          {c.result !== 'superseded' && c.result !== 'yes' && c.result !== 'no'
            ? ` — ${OUTCOME_TEXT[c.outcome] ?? c.outcome}`
            : ''}
          {c.reason ? (
            <span className="block text-xs text-bluesphere-muted">{c.reason}</span>
          ) : null}
          <span className="block text-xs text-bluesphere-muted">{timeAgo(c.at)}</span>
        </p>
      ) : (
        <p className="mb-3 text-sm text-bluesphere-muted">
          Waiting for an answer until {new Date(outreach.deadline).toLocaleTimeString()}.
        </p>
      )}
      {parties.length > 0 && (
        <ul className="divide-y divide-bluesphere-border text-sm">
          {parties.map((p) => (
            <li key={p.role} className="flex flex-wrap items-start justify-between gap-2 py-2">
              <span className="min-w-0">
                <span className="font-medium">{ROLE_TEXT[p.role] ?? p.role}</span>{' '}
                <span className="text-bluesphere-muted">{p.principal}</span>
                {p.role === 'contact' && (
                  <span className="block text-xs text-bluesphere-muted">
                    Asked at half the window, when nobody had answered
                  </span>
                )}
                <span className="block text-xs text-bluesphere-muted">
                  {[p.phone && `phone ${p.phone}`, p.email && `email ${p.email}`]
                    .filter(Boolean)
                    .join(' · ') || 'no address on file'}
                </span>
                <Sends party={p} />
              </span>
              <span className="text-right">{answerText(p, c !== null)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
